"use client";

import { useEffect, useState } from "react";
import { Info, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { MoatOverallDirection, MoatTrendResult, TrendDirection } from "@/lib/quality/moat-trend";

const OVERALL_LABEL: Record<MoatOverallDirection, string> = {
  improving: "Fundamental verbessernd",
  declining: "Fundamental verschlechternd",
  mixed: "Gemischtes Bild",
  stable: "Fundamental stabil",
};

const OVERALL_COLOR: Record<MoatOverallDirection, string> = {
  improving: "text-positive",
  declining: "text-negative",
  mixed: "text-gold-text",
  stable: "text-ink-secondary",
};

function DirectionIcon({ direction }: { direction: TrendDirection }) {
  if (direction === "improving") return <TrendingUp className="h-4 w-4 text-positive" />;
  if (direction === "declining") return <TrendingDown className="h-4 w-4 text-negative" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

/**
 * Moat-/Qualitäts-Trend-Kachel (backlog.md -> D8) - verdichtet mehrere Margen-/
 * Renditetrends zu einer Aussage "wird das Unternehmen fundamental besser oder
 * schlechter". Balken je Kennzahl animieren beim ersten Rendern ein (gleiches
 * pathLength-freies Breiten-Animationsmuster wie andere Fortschrittsbalken im Projekt),
 * bewusst zurückhaltend statt aufwendig - reine Übersichtskachel, kein Flagship-Feature.
 */
export function MoatTrendPanel({ result }: { result: MoatTrendResult }) {
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setAnimated(true), 0);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-secondary">
        Moat-/Qualitäts-Trend ({result.lookbackYears}J)
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" aria-label="Erklärung: Moat-/Qualitäts-Trend" className="text-muted-foreground hover:text-ink">
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-72">
            Verdichtet Bruttomarge, Nettomarge, ROIC, Eigenkapitalrendite und FCF-Marge zu
            einer Aussage, ob sich die fundamentale Verfassung des Unternehmens über die
            letzten {result.lookbackYears} Jahre verbessert oder verschlechtert hat - ein
            Näherungswert für die Entwicklung des Wettbewerbsvorteils („Moat“), keine
            Bewertungsaussage.
          </TooltipContent>
        </Tooltip>
      </span>

      <span className={cn("text-base font-medium", OVERALL_COLOR[result.overallDirection])}>
        {OVERALL_LABEL[result.overallDirection]}
      </span>

      <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {result.metrics.map((m) => {
          const barWidth = animated ? Math.min(100, Math.abs(m.change) * 1000) : 0;
          return (
            <div key={m.key} className="flex items-center gap-2 text-sm">
              <DirectionIcon direction={m.direction} />
              <span className="w-28 shrink-0 truncate text-ink-secondary" title={m.label}>
                {m.label}
              </span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-700 ease-out motion-reduce:duration-0",
                    m.direction === "improving" && "bg-positive",
                    m.direction === "declining" && "bg-negative",
                    m.direction === "stable" && "bg-muted-foreground/40",
                  )}
                  style={{ width: `${barWidth}%` }}
                />
              </div>
              <span className="w-16 shrink-0 text-right font-tabular text-xs text-ink-secondary">
                {m.change >= 0 ? "+" : ""}
                {(m.change * 100).toFixed(1)} Pp
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
